import {Injectable} from "@angular/core";
import {Action, Store} from "@ngrx/store";
import * as fromRoot from "../../../../state/app.state";
import {InviteService} from "./invite.service";
import {Actions, createEffect, ofType} from "@ngrx/effects";
import {Observable, of} from "rxjs";
import {catchError, map, mergeMap, tap} from "rxjs/operators";
import {
  FetchAdultUsernameFilterFailure,
  FetchAdultUsernameFilterRequest,
  FetchAdultUsernameFilterSuccess,
  FetchClientFilterFailure,
  FetchClientFilterListSuccess,
  FetchClientFilterRequest,
  FetchEmailPreviewDataFailure,
  FetchEmailPreviewDataRequest,
  FetchEmailPreviewDataSuccess,
  FetchGroupFilterFailure,
  FetchGroupFilterSuccess,
  FetchGroupsFilterRequest,
  FetchParticipantUsernameFilterFailure,
  FetchParticipantUsernameFilterRequest,
  FetchParticipantUsernameFilterSuccess, FetchPreviewInviteDataRequest,
  HasAssociatedLarFailure,
  HasAssociatedLarRequest,
  HasAssociatedLarSuccess,
  InviteUserFailure,
  InviteUserRequest,
  InviteUserSuccess,
  ReleaseUsernameFailure,
  ReleaseUsernameRequest,
  ReleaseUsernameSuccess,
  SetEmailPreviewData
} from './invite.actions';
import {InviteUserModel} from "../models/invite-user.model";
import {Invite} from "../services/invite";
import {
  ParticipantUsernameFilter
} from "../models/participant-username-filter.model";
import {AdultUsernameFilter} from "../models/adult-username-filter.model";
import {ClientOptionsModel} from "../models/client-options.model";
import {CommonService} from "../../../shared/services/common.service";
import {
  EmailPreviewComponent
} from "../components/email-preview/email-preview.component";
import {emailPreviewDataInfo} from "./invite.selectors";
import {HasAssociatedLarModel} from "../models/has-associated-lar.model";
import {GroupOptionsModel} from "../models/group-options.model";

@Injectable()
export class InviteEffects {

  fetchGroupFilter$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchGroupsFilterRequest),
      map((action) => {
        return action.payload;
      }),
      mergeMap((payload) =>
        this.inviteService.fetchGroupsFilter(payload).pipe(
          map((response: GroupOptionsModel['response']) => {
            const {data, message} = response;
            return FetchGroupFilterSuccess({
              groupFilter: data,
              message
            });
          }),
          catchError(() => {
            return of(FetchGroupFilterFailure());
          }),
          tap((action) => {
            if (action.type === FetchGroupFilterSuccess.type) {
              // Code to execute on API Success Action dispatch

            } else if (action.type === FetchGroupFilterFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  fetchClientFilter$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchClientFilterRequest),
      mergeMap((payload) =>
        this.inviteService.fetchClientsFilter(payload.payload).pipe(
          map((response: ClientOptionsModel['response']) => {
            const {data, message} = response;

            return FetchClientFilterListSuccess({
              clientFilter: data,
              message
            });
          }),
          catchError(() => {
            return of(FetchClientFilterFailure());
          }),
          tap((action) => {
            if (action.type === FetchClientFilterListSuccess.type) {
              // Code to execute on API Success Action dispatch

            } else if (action.type === FetchClientFilterFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  inviteUser$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(InviteUserRequest),
      map((action) => {
        return action.payload;
      }),
      mergeMap((payload) =>
        this.inviteService.invite(payload).pipe(
          map((response: InviteUserModel["response"]) => {
            const {data, message} = response;
            if (data && Array.isArray(data)) {
              this.store.dispatch(SetEmailPreviewData({emailPreviewData: data}));
            }
            return InviteUserSuccess({
              data, message
            });
          }),
          catchError(() => {
            return of(InviteUserFailure());
          }),
          tap((action) => {
            if (action.type === InviteUserSuccess.type) {
              // Code to execute on API Success Action dispatch
              if (!(action.data && Array.isArray(action.data))) {
                this.invite.resetForm.next();
                this.commonService.updateQueryParams({
                  'clientId': null
                });
              }

              this.commonService.notification(
                action.message,
                'success'
              );
            } else if (action.type === InviteUserFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  fetchParticipantUsernameList$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchParticipantUsernameFilterRequest),
      map((action) => {
        return action.payload;
      }),
      mergeMap((payload: ParticipantUsernameFilter['request']) =>
        this.inviteService.fetchParticipantUsernameFilter(payload).pipe(
          map((response: ParticipantUsernameFilter["response"]) => {
            const {data, message} = response;
            const participantUsernameFilter = data;
            return FetchParticipantUsernameFilterSuccess({
              participantUsernameFilter,
              message
            });
          }),
          catchError(() => {
            return of(FetchParticipantUsernameFilterFailure());
          }),
          tap((action) => {
            if (action.type === FetchParticipantUsernameFilterSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchParticipantUsernameFilterFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  fetchAdultUsernameList$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchAdultUsernameFilterRequest),
      map((action) => {
        return action.payload;
      }),
      mergeMap((payload: AdultUsernameFilter['request']) =>
        this.inviteService.fetchAdultUsernameFilter(payload).pipe(
          map((response: AdultUsernameFilter["response"]) => {
            const {data, message} = response;
            const adultUsernameFilter = data;
            return FetchAdultUsernameFilterSuccess({
              adultUsernameFilter,
              message
            });
          }),
          catchError(() => {
            return of(FetchAdultUsernameFilterFailure());
          }),
          tap((action) => {
            if (action.type === FetchAdultUsernameFilterSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === FetchAdultUsernameFilterFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  hasAssociatedLar$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(HasAssociatedLarRequest),
      map((action) => {
        return action.payload;
      }),
      mergeMap((payload: HasAssociatedLarModel['request']) =>
        this.inviteService.hasAssociatedLar(payload).pipe(
          map((response: HasAssociatedLarModel["response"]) => {
            const {data, message} = response;
            const hasAssociatedLar = data.hasAssociatedLar;
            return HasAssociatedLarSuccess({
              hasAssociatedLar,
              message
            });
          }),
          catchError(() => {
            return of(HasAssociatedLarFailure());
          }),
          tap((action) => {
            if (action.type === HasAssociatedLarSuccess.type) {
              // Code to execute on API Success Action dispatch
              this.invite.isExpandAdultSection.next(true);
            } else if (action.type === HasAssociatedLarFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  releaseUsername: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(ReleaseUsernameRequest),
      map((action) => {
        return action.payload;
      }),
      mergeMap((payload) =>
        this.inviteService.releaseUsername(payload).pipe(
          map((response: any) => {
            return ReleaseUsernameSuccess();
          }),
          catchError(() => {
            return of(ReleaseUsernameFailure());
          }),
          tap((action) => {
            if (action.type === ReleaseUsernameSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === ReleaseUsernameFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  emailPreviewData;
  fetchEmailPreviewData$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchEmailPreviewDataRequest),
      map((action) => {
        return action.payload;
      }),
      mergeMap((payload) =>
        this.inviteService.fetchEmailPreviewData(payload).pipe(
          map((response: any) => {
            const {data, message} = response;
            return FetchEmailPreviewDataSuccess({
              emailPreviewData: data,
              message
            });
          }),
          catchError(() => {
            return of(FetchEmailPreviewDataFailure());
          }),
          tap((action) => {
            if (action.type === FetchEmailPreviewDataSuccess.type) {
              // Code to execute on API Success Action dispatch
              this.store.select(emailPreviewDataInfo)
                .subscribe(data => {
                  this.emailPreviewData = data
                });

              setTimeout(() => {
                this.commonService.openDialog({
                  data: {
                    component: EmailPreviewComponent,
                    data: this.emailPreviewData
                  }
                })
              }, 100);

            } else if (action.type === FetchEmailPreviewDataFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  fetchPreviewInviteData$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchPreviewInviteDataRequest),
      map((action) => {
        this.commonService.startLoading();
        return action;
      }),
      mergeMap((payload) =>
        this.inviteService.fetchPreviewInviteData(payload).pipe(
          map((response: any) => {
            const {data, message} = response;
            return FetchEmailPreviewDataSuccess({
              emailPreviewData: [data],
              message
            });
          }),
          catchError(() => {
            return of(FetchEmailPreviewDataFailure());
          }),
          tap((action) => {
            this.commonService.stopLoading();
            if (action.type === FetchEmailPreviewDataSuccess.type) {
              // Code to execute on API Success Action dispatch
              this.store.select(emailPreviewDataInfo)
                .subscribe(data => {
                  this.emailPreviewData = data
                });

              setTimeout(() => {
                this.commonService.openDialog({
                  data: {
                    component: EmailPreviewComponent,
                    data: {
                      ...this.emailPreviewData,
                    },
                    fromManageUsers: true,
                  }
                })
              }, 100);

            } else if (action.type === FetchEmailPreviewDataFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  constructor(
    private store: Store<fromRoot.State>,
    private inviteService: InviteService,
    private actions: Actions,
    private invite: Invite,
    private commonService: CommonService
  ) {
  }
}
